import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ExternalLink,
  Image,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import {
  fetchAdminBanners,
  fetchAdminSection,
  patchAdminSection,
  type AdminBannerRow,
} from '../../api/admin';
import {
  AdminDetailCard,
  AdminEmptyState,
  AdminKpiStrip,
  AdminPageLayout,
  AdminSearchInput,
  AdminStatusBadge,
  AdminTablePanel,
} from '../../components/admin/AdminPageLayout';
import { Illustration } from '../../components/common/Illustration';
import { LoadingState, ErrorState } from '../../components/states/StateViews';

const ILLUSTRATION_OPTIONS = [
  'hero.home',
  'hero.results',
  'hero.vault',
  'hero.admit-cards',
  'hero.dashboard',
  'hero.notifications',
  'hero.jobs',
  'feature.vault',
  'feature.results',
  'admin.module.cms',
];

type BannerConfig = Record<string, unknown> & {
  kicker?: string;
  illustration?: string;
  primaryCta?: { label?: string; route?: string; icon?: string };
  secondaryCta?: { label?: string; route?: string };
  cta?: { label?: string; route?: string; icon?: string };
};

function bannerTitle(row: AdminBannerRow) {
  if (row.title) return row.title;
  if (row.sectionType === 'hero') return `${row.pageLabel ?? row.page} hero`;
  return row.id;
}

function BannerCard({
  row,
  onSelect,
}: {
  row: AdminBannerRow;
  onSelect: () => void;
}) {
  const illustration = row.illustration ?? (row.page === 'home' ? 'hero.home' : 'admin.module.cms');

  return (
    <button type="button" className="wfx-admin-banner-card glass-surface" onClick={onSelect}>
      <Illustration context={illustration} size="sm" className="wfx-admin-page-card-illustration" />
      <div className="wfx-admin-page-card-copy">
        <strong>{bannerTitle(row)}</strong>
        <span className="wfx-muted">{row.pageLabel ?? row.page} · {row.sectionTypeLabel ?? row.sectionType}</span>
      </div>
      <div className="wfx-admin-page-card-stats">
        {row.kicker && <span className="chip-pill">{row.kicker}</span>}
        <AdminStatusBadge status={row.isEnabled ? 'enabled' : 'disabled'} />
        {row.primaryCtaLabel && <span>{row.primaryCtaLabel}</span>}
      </div>
    </button>
  );
}

function BannerDetailCard({
  bannerId,
  onClose,
}: {
  bannerId: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-section', bannerId],
    queryFn: () => fetchAdminSection(bannerId!),
    enabled: Boolean(bannerId),
  });

  const update = useMutation({
    mutationFn: (body: Parameters<typeof patchAdminSection>[1]) => patchAdminSection(bannerId!, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-banners'] });
      void qc.invalidateQueries({ queryKey: ['admin-section', bannerId] });
      void qc.invalidateQueries({ queryKey: ['admin-sections'] });
    },
  });

  const section = data?.section;
  const config = (section?.configuration ?? {}) as BannerConfig;
  const configPreview = section?.configuration
    ? JSON.stringify(section.configuration, null, 2)
    : '{}';
  const illustration = section?.configuration?.illustration as string | undefined
    ?? (section?.page === 'home' ? 'hero.home' : 'admin.module.cms');
  const primaryCta = config.primaryCta ?? config.cta;

  const patchConfig = (patch: BannerConfig) => {
    update.mutate({
      configuration: { ...config, ...patch },
    });
  };

  const patchPrimaryCta = (patch: { label?: string; route?: string }) => {
    const key = config.cta ? 'cta' : 'primaryCta';
    patchConfig({
      [key]: { ...(config[key] as object), ...patch },
    });
  };

  const patchSecondaryCta = (patch: { label?: string; route?: string }) => {
    patchConfig({
      secondaryCta: { ...(config.secondaryCta ?? {}), ...patch },
    });
  };

  return (
    <AdminDetailCard
      open={Boolean(bannerId)}
      onClose={onClose}
      ariaLabel="Banner details"
      breadcrumb="CMS banner"
      title={section ? bannerTitle(section as AdminBannerRow) : 'Loading…'}
      illustration={illustration}
      subtitle={
        section ? (
          <>
            <AdminStatusBadge status={section.isEnabled ? 'enabled' : 'disabled'} />
            <span className="wfx-muted wfx-admin-subline">
              {section.sectionTypeLabel ?? section.sectionType} · {section.id}
            </span>
          </>
        ) : undefined
      }
    >
      {isLoading && <LoadingState label="Loading banner…" />}
      {isError && <ErrorState message="Could not load banner." onRetry={() => void refetch()} />}

      {section && (
        <>
          <dl className="wfx-admin-meta-list wfx-admin-meta-list--grid">
            <div><dt>Page</dt><dd>{section.pageLabel ?? section.page}</dd></div>
            <div><dt>Type</dt><dd>{section.sectionTypeLabel ?? section.sectionType}</dd></div>
            <div><dt>Order</dt><dd>{section.displayOrder}</dd></div>
            <div>
              <dt>Public route</dt>
              <dd>
                {section.pageRoute && (
                  <Link to={section.pageRoute} className="secondary-action compact" target="_blank">
                    {section.pageRoute}
                    <ExternalLink size={12} aria-hidden="true" />
                  </Link>
                )}
              </dd>
            </div>
          </dl>

          <div className="wfx-admin-drawer-columns">
            <div className="wfx-admin-drawer-section">
              <h3>Headline copy</h3>
              <div className="wfx-admin-form">
                <label className="wfx-admin-field">
                  <span>Title</span>
                  <input
                    defaultValue={section.title ?? ''}
                    placeholder={section.sectionType === 'hero' ? 'Uses site tagline when empty' : ''}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value !== (section.title ?? '')) update.mutate({ title: value || null });
                    }}
                  />
                </label>
                <label className="wfx-admin-field">
                  <span>Subtitle</span>
                  <textarea
                    rows={3}
                    className="wfx-admin-textarea"
                    defaultValue={section.subtitle ?? ''}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value !== (section.subtitle ?? '')) update.mutate({ subtitle: value || null });
                    }}
                  />
                </label>
                <label className="wfx-admin-field">
                  <span>Kicker</span>
                  <input
                    defaultValue={config.kicker ?? ''}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value !== (config.kicker ?? '')) patchConfig({ kicker: value || undefined });
                    }}
                  />
                </label>
                <label className="wfx-admin-field">
                  <span>Illustration</span>
                  <select
                    defaultValue={config.illustration ?? ''}
                    onChange={(e) => patchConfig({ illustration: e.target.value || undefined })}
                  >
                    <option value="">Default</option>
                    {ILLUSTRATION_OPTIONS.map((ctx) => (
                      <option key={ctx} value={ctx}>{ctx}</option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="secondary-action compact"
                  disabled={update.isPending}
                  onClick={() => update.mutate({ isEnabled: !section.isEnabled })}
                >
                  {section.isEnabled ? (
                    <><ToggleRight size={16} aria-hidden="true" /> Disable banner</>
                  ) : (
                    <><ToggleLeft size={16} aria-hidden="true" /> Enable banner</>
                  )}
                </button>
              </div>
            </div>

            <div className="wfx-admin-drawer-section">
              <h3>Calls to action</h3>
              <div className="wfx-admin-form">
                <label className="wfx-admin-field">
                  <span>Primary CTA label</span>
                  <input
                    defaultValue={primaryCta?.label ?? ''}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value !== (primaryCta?.label ?? '')) patchPrimaryCta({ label: value });
                    }}
                  />
                </label>
                <label className="wfx-admin-field">
                  <span>Primary CTA route</span>
                  <input
                    defaultValue={primaryCta?.route ?? ''}
                    placeholder="/results"
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value !== (primaryCta?.route ?? '')) patchPrimaryCta({ route: value });
                    }}
                  />
                </label>
                {(section.sectionType === 'hero' || section.sectionType === 'page_header') && (
                  <>
                    <label className="wfx-admin-field">
                      <span>Secondary CTA label</span>
                      <input
                        defaultValue={config.secondaryCta?.label ?? ''}
                        onBlur={(e) => {
                          const value = e.target.value.trim();
                          if (value !== (config.secondaryCta?.label ?? '')) {
                            patchSecondaryCta({ label: value });
                          }
                        }}
                      />
                    </label>
                    <label className="wfx-admin-field">
                      <span>Secondary CTA route</span>
                      <input
                        defaultValue={config.secondaryCta?.route ?? ''}
                        onBlur={(e) => {
                          const value = e.target.value.trim();
                          if (value !== (config.secondaryCta?.route ?? '')) {
                            patchSecondaryCta({ route: value });
                          }
                        }}
                      />
                    </label>
                  </>
                )}
              </div>

              <h3>Raw configuration</h3>
              <pre className="wfx-admin-config-preview">{configPreview}</pre>
            </div>
          </div>
        </>
      )}
    </AdminDetailCard>
  );
}

export function AdminBannersScreen() {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [pageFilter, setPageFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-banners', debouncedQuery, pageFilter, typeFilter, statusFilter],
    queryFn: () =>
      fetchAdminBanners({
        q: debouncedQuery || undefined,
        page: pageFilter,
        type: typeFilter,
        status: statusFilter,
      }),
  });

  const rows = data?.data ?? [];
  const stats = data?.stats;
  const pageOptions = data?.pages ?? [];

  const kpiItems = useMemo(
    () => [
      { label: 'Total banners', value: stats?.total ?? 0 },
      { label: 'Enabled', value: stats?.enabled ?? 0 },
      { label: 'Heroes', value: stats?.heroes ?? 0 },
      { label: 'Pages covered', value: stats?.pages ?? 0 },
    ],
    [stats],
  );

  if (isLoading && !data) return <LoadingState label="Loading banners…" />;
  if (isError) return <ErrorState message="Could not load banners." onRetry={() => void refetch()} />;

  return (
    <>
      <AdminPageLayout
        breadcrumb="Admin / Content / Banners"
        title="Banners"
        description="Manage hero blocks, page headers, and CTA banners — headlines, kickers, illustrations, and calls to action."
        icon={Image}
        illustration="admin.module.cms"
      >
        <AdminKpiStrip items={kpiItems} />

        <div className="wfx-admin-faq-section-grid">
          {rows.map((row) => (
            <BannerCard key={row.id} row={row} onSelect={() => setSelectedId(row.id)} />
          ))}
        </div>
        {rows.length === 0 && <AdminEmptyState message="No banners match your filters." />}

        <AdminTablePanel
          title="All banners"
          toolbar={(
            <div className="wfx-admin-toolbar">
              <AdminSearchInput
                value={query}
                onChange={setQuery}
                placeholder="Search title, page, or id…"
              />
              <select
                className="wfx-admin-select"
                value={pageFilter}
                onChange={(e) => setPageFilter(e.target.value)}
                aria-label="Filter by page"
              >
                <option value="all">All pages</option>
                {pageOptions.map((page) => (
                  <option key={page.id} value={page.id}>{page.label}</option>
                ))}
              </select>
              <select
                className="wfx-admin-select"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                aria-label="Filter by banner type"
              >
                <option value="all">All types</option>
                <option value="hero">Hero</option>
                <option value="page_header">Page header</option>
                <option value="cta_banner">CTA banner</option>
              </select>
              <select
                className="wfx-admin-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="Filter by status"
              >
                <option value="all">All status</option>
                <option value="enabled">Enabled</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
          )}
        >
          <table className="wfx-admin-table">
            <thead>
              <tr><th>Title</th><th>Page</th><th>Type</th><th>Kicker</th><th>Primary CTA</th><th>Status</th></tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="wfx-admin-table-row-clickable"
                  onClick={() => setSelectedId(row.id)}
                >
                  <td><strong>{bannerTitle(row)}</strong></td>
                  <td>{row.pageLabel ?? row.page}</td>
                  <td>{row.sectionTypeLabel ?? row.sectionType}</td>
                  <td>{row.kicker ?? '—'}</td>
                  <td>{row.primaryCtaLabel ?? '—'}</td>
                  <td><AdminStatusBadge status={row.isEnabled ? 'enabled' : 'disabled'} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTablePanel>
      </AdminPageLayout>

      <BannerDetailCard bannerId={selectedId} onClose={() => setSelectedId(null)} />
    </>
  );
}
